import React from 'react';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import './HomeGreetingV2.css';

export interface HomeGreetingV2Props {
  /** Authenticated user display name */
  name: string;
  /** When true, renders a skeleton placeholder instead of the greeting */
  isLoading?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * HomeGreetingV2
 *
 * Greeting row of the new home: name + wave on the left, welcome sentence on
 * the right. Figma node `1601:13659`.
 *
 * @example
 * ```tsx
 * <HomeGreetingV2 name="Maria Cristina" />
 * ```
 *
 * @since 1.0.0
 * @category Organisms
 */
export const HomeGreetingV2: React.FC<HomeGreetingV2Props> = ({
  name,
  isLoading = false,
  className = '',
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <header
        className={`home-greeting-v2 ${className}`.trim()}
        aria-label={t('Initial greeting')}
      >
        <Skeleton.Input active style={{ width: 280, height: 28 }} />
      </header>
    );
  }

  return (
    <header
      className={`home-greeting-v2 ${className}`.trim()}
      aria-label={t('Initial greeting')}
    >
      <div className="home-greeting-v2__name-block">
        <h3 className="home-greeting-v2__title">
          {t('Hello, {{name}}', { name })}
        </h3>
        {/* TODO(figma): replace with the exported 👋 PNG asset (1601:13669) once
            figma.com binary asset downloads are allowed by the network policy. */}
        <span className="home-greeting-v2__wave" aria-hidden="true">
          👋
        </span>
      </div>
      <p className="home-greeting-v2__intro">
        {t('Welcome(a) to')} <strong>{t('Identity and Fraud Platform')}</strong>{' '}
        {t('from Serasa Experian.')}
      </p>
    </header>
  );
};

export default HomeGreetingV2;
